<script setup lang="ts">
  import { onMounted, onUnmounted } from 'vue'
  import { useCurrenciesModals, useCurrenciesProvider } from '../providers'
  import { testIds } from '@gat-ship/types'
  import {
    Button,
    DataList,
    DataListItem,
    DataListItemAction,
    DataListItemActions,
    DataListItemTitle,
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    Empty,
    EmptyContent,
    EmptyDescription,
    EmptyHeader,
    EmptyMedia,
    EmptyTitle,
    FlagIcon,
    useAppLoading
  } from '@gat-ship/vue-components'
  import {
    IconCurrency,
    IconLoading,
    IconPlus,
    IconTrash
  } from '@gat-ship/icons'

  // List body for the AdministrationModal currencies panel. The collection
  // fetch + SignalR subscription live here. The empty state offers its own Add
  // button; the panel hides its header Add when the list is empty, so the two
  // Adds never show at once.
  const provider = useCurrenciesProvider()
  const currenciesModals = useCurrenciesModals()!

  const loading = useAppLoading()

  onMounted(async () => {
    await provider.fetchCurrencies()
    provider.signalR.subscribeToCurrencyEvents()
  })

  onUnmounted(() => {
    provider.signalR.unsubscribeFromCurrencyEvents()
  })
</script>

<template>
  <div
    v-if="loading.isLoading('currencies')"
    class="text-muted-foreground mx-auto flex items-center justify-center gap-2 p-6"
  >
    <IconLoading class="size-6" /> Fetching currencies...
  </div>
  <Empty v-else-if="provider.currencies.value?.length === 0">
    <EmptyHeader>
      <EmptyMedia variant="icon">
        <IconCurrency />
      </EmptyMedia>
      <EmptyTitle>No currencies yet.</EmptyTitle>
      <EmptyDescription> Add a currency to get started. </EmptyDescription>
    </EmptyHeader>
    <EmptyContent>
      <Button
        :data-testid="testIds.currency.action.addCurrency"
        @click="currenciesModals.showAddCurrency({})"
      >
        <IconPlus class="size-4" />
        Add Currency
      </Button>
    </EmptyContent>
  </Empty>
  <DataList v-else>
    <DataListItem
      v-for="currency in provider.currencies.value ?? []"
      :key="currency.code"
      @click:item="currenciesModals.showCurrencyDetails({ currency })"
    >
      <div class="flex w-full items-center gap-2">
        <div :class="'text-muted-foreground size-6 rounded-full'">
          <IconLoading v-if="loading.isLoading('currency-' + currency.code)" />
          <FlagIcon v-else :class="'rounded-full'" :flag="currency.flagCode" />
        </div>
        <DataListItemTitle>
          <div class="flex gap-1 text-sm">
            <span
              :data-testid="testIds.currency.list.currencyCode(currency.code)"
              >{{ currency.code }}</span
            >
            <span class="text-muted-foreground font-normal">
              - {{ currency.name }}</span
            >
          </div>
        </DataListItemTitle>
        <div class="ml-auto flex items-center gap-2">
          <div class="flex flex-col items-center gap-0.5 text-center">
            <div class="text-sm font-medium">
              {{ currency.minimalAmount }}
            </div>
            <div class="text-muted-foreground text-xs">Min. Amount</div>
          </div>
        </div>
      </div>
      <template #actions>
        <DataListItemActions>
          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <DataListItemAction />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                :data-testid="testIds.currency.action.removeCurrency"
                @click="currenciesModals.showRemoveCurrency({ currency })"
              >
                <IconTrash class="size-4" />
                Remove Currency
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </DataListItemActions>
      </template>
    </DataListItem>
  </DataList>
</template>
